<script lang="ts">
  import { deleteSearchQueryStringParameter, setQueryStringParameter } from "../../url";
  import { SearchState } from "../../services/search-service";
  import { fade } from "svelte/transition";
  import type { IdNameTuple } from "../../models/common.interface";
  import type { SearchStateModel } from "../../models/search.interface";
  import Filter from "../Filter.svelte";
  import TooltipHybridButton from "./TooltipHybridButton.svelte";

  export let searchUrlQueryParam: string;
  export let values: IdNameTuple[] = null;
  export let url: URL | null = null;
  export let title: string;
  export let tooltip: string | null;

  // control whether checkbox list is collapsable or not,
  export let collapsable: boolean | null = null;
  let filterCollapsed = false;
  let statusMessage = "";

  let searchTerm = ""; // var bound to Filter component below
  $: searchItems = values ? values.filter(item =>
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) || item.id?.toLowerCase().includes(searchTerm.toLowerCase())
  ) : []

  let activeSearchParams = [];
  SearchState.subscribe((search: SearchStateModel) => {
    activeSearchParams = search.params.getAll(searchUrlQueryParam) ? search.params.getAll(searchUrlQueryParam) : [];
  });

  if (url !== null) {
    fetchSearchValues(url);
  }

  async function fetchSearchValues(url: URL): Promise<IdNameTuple[]> {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch search values");

      const searchValues = await response.json();
      values = searchValues?.results as IdNameTuple[];
      return searchValues;
    } catch (err) {
      console.error("Failed to fetch search values:", err);
      throw err;
    }
  }

  function onChange(id: string) {
    if (!activeSearchParams.includes(id)) {
      setQueryStringParameter(searchUrlQueryParam, id, true);
    } else {
      deleteSearchQueryStringParameter(searchUrlQueryParam, id);
    }
  }

  function handleCollapseToggle() {
    filterCollapsed = !filterCollapsed;
    statusMessage = filterCollapsed
            ? "Keyword filter options expanded. Use arrow keys to navigate."
            : "Keyword filter options collapsed.";

    // Clear status message after announcement
    setTimeout(() => {
      statusMessage = "";
    }, 1000);
  }
</script>

<li class="mb-1">
  <div class="pb-2 d-flex align-items-center justify-content-between">
    <div>
      {title}
      <!-- tooltip icon -->
      <TooltipHybridButton
        searchUrlQueryParam={searchUrlQueryParam}
        tooltip={tooltip}
      />
    </div>
  </div>

  {#if collapsable}
    <div class="d-grid gap-2">
      <button
              aria-controls="filterCollapse"
              aria-expanded={filterCollapsed}
              aria-label={filterCollapsed
              ? "Collapse filter search options"
              : "Expand filter search options"}
              class="btn ae-primary btn-lg collapsed"
              class:filterCollapsed
              data-bs-target="#filterCollapse"
              data-bs-toggle="collapse"
              on:click={handleCollapseToggle}
              type="button"
      >
        <i class="bi bi-caret-down icon"></i>
      </button>
    </div>
    <!-- ARIA live region for status messages -->
    <div aria-live="polite" aria-atomic="true" class="visually-hidden">
      {statusMessage}
    </div>
  {/if}

  <div
    style="max-height: 255px; overflow-y: auto; border-bottom: 2px solid #B0BBBB;"
    class="px-1 collapse {collapsable ? '' : 'show'}"
    id="filterCollapse"
    role="region"
    aria-label="{searchUrlQueryParam} filter container"
  >
    <!-- text input filter for list of keywords, if the list is longer than 10 -->
    {#if values && values.length > 10}
      <Filter
        bind:searchTerm={searchTerm}
        id="filter-datatype"
        title="DataType Filter Box"
        ariaLabel="Filter data types by name"
      />
    {/if}
    <!-- if no results in filtered list -->
    {#if searchTerm && searchItems.length === 0}
      <div class="p-2 text-center text-muted" transition:fade>
        No results match "{searchTerm}"
      </div>
    {:else}
      <form id="{searchUrlQueryParam}-form">
        <!-- checkbox list w/ label -->
        {#each searchItems || [] as d (d.id)}
          <div class="form-check" transition:fade>
            <input
              class="form-check-input"
              type="checkbox"
              value={d.id}
              id="{searchUrlQueryParam}-{d.id}"
              checked={activeSearchParams.includes(d.id)}
              on:change={() => onChange(d.id)}
            />
            <label class="form-check-label" for="{searchUrlQueryParam}-{d.id}">
              {d.name}
            </label>
          </div>
        {/each}
      </form>
    {/if}
  </div>
  {#if $SearchState.params.has(searchUrlQueryParam)}
    {#each activeSearchParams || [] as d}
      <div class="d-flex py-1">
        <span
          style="overflow-x: hidden; text-overflow: ellipsis"
          class="badge rounded-pill text-bg-dark pb-0 pe-3 pt-1"
          role="status"
          aria-label="{searchUrlQueryParam} search pill: {values.find((x) => x.id === d)?.name.toLowerCase()}"
        >
          <button
            type="button"
            class="btn p-0 m-0"
            on:click={() => deleteSearchQueryStringParameter(searchUrlQueryParam, d)}
          >
            <i
              class="bi bi-x text-bg-dark"
              aria-label="Delete {searchUrlQueryParam} {values.find((x) => x.id === d)?.name.toLowerCase()}"
            ></i>
          </button>
          {values.find((x) => x.id === d)?.name.toLowerCase()}
        </span>
      </div>
    {/each}
  {/if}
</li>

<style>

  .filterCollapsed {
    transform: rotate(180deg);
  }

</style>
